import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { LinkProps, NavigateOptions, To } from 'react-router-dom';

import { DiscardChangesDialog } from '../components/DiscardChangesDialog';

/**
 * One guard for the whole Studio shell. It is mounted at StudioPage (not inside AI settings) so the
 * sidebar and in-page links can ask before leaving a dirty form.
 *
 * Browser Back is not intercepted: Studio uses BrowserRouter, and react-router's useBlocker needs a
 * data router. Reload and tab close are covered by beforeunload.
 */

type Registration = {
  id: symbol;
  label: string;
  isDirty: boolean;
  discard: () => void;
};

type PendingNavigation = { to: To; options?: NavigateOptions };

type UnsavedChangesContextValue = {
  isDirty: boolean;
  guardedNavigate: (to: To, options?: NavigateOptions) => void;
  register: (registration: Registration) => void;
  unregister: (id: symbol) => void;
};

const UnsavedChangesContext = createContext<UnsavedChangesContextValue | null>(null);

export function UnsavedChangesProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const [registration, setRegistration] = useState<Registration | null>(null);
  const [pending, setPending] = useState<PendingNavigation | null>(null);
  const isDirty = !!registration?.isDirty;

  const register = useCallback((next: Registration) => setRegistration(next), []);
  const unregister = useCallback(
    (id: symbol) => setRegistration((current) => (current?.id === id ? null : current)),
    []
  );

  const guardedNavigate = useCallback(
    (to: To, options?: NavigateOptions) => {
      if (isDirty) {
        setPending({ to, options });
        return;
      }
      navigate(to, options);
    },
    [isDirty, navigate]
  );

  useEffect(() => {
    if (!isDirty) return undefined;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Chrome still requires returnValue to show the native prompt.
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const handleDiscard = () => {
    if (!pending) return;
    registration?.discard();
    setRegistration(null);
    setPending(null);
    navigate(pending.to, pending.options);
  };

  const value = useMemo(
    () => ({ isDirty, guardedNavigate, register, unregister }),
    [isDirty, guardedNavigate, register, unregister]
  );

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
      <DiscardChangesDialog
        isOpen={!!pending}
        sectionLabel={registration?.label ?? 'this page'}
        onKeepEditing={() => setPending(null)}
        onDiscard={handleDiscard}
      />
    </UnsavedChangesContext.Provider>
  );
}

/** Navigate through the guard when one is mounted, plain navigate otherwise. */
export function useGuardedNavigate() {
  const context = useContext(UnsavedChangesContext);
  const navigate = useNavigate();
  return context?.guardedNavigate ?? navigate;
}

export function useHasUnsavedChanges() {
  return !!useContext(UnsavedChangesContext)?.isDirty;
}

/** A page registers its dirty state while mounted. `label` names the section in the dialog. */
export function useRegisterUnsavedChanges({
  label,
  isDirty,
  discard,
}: {
  label: string;
  isDirty: boolean;
  discard: () => void;
}) {
  const context = useContext(UnsavedChangesContext);
  const idRef = useRef(Symbol(label));
  const discardRef = useRef(discard);
  discardRef.current = discard;

  const register = context?.register;
  const unregister = context?.unregister;

  useEffect(() => {
    register?.({ id: idRef.current, label, isDirty, discard: () => discardRef.current() });
  }, [register, label, isDirty]);

  useEffect(() => {
    const id = idRef.current;
    return () => unregister?.(id);
  }, [unregister]);
}

/** A react-router Link that asks before leaving a dirty form. */
export function GuardedLink({ to, onClick, ...props }: LinkProps) {
  const context = useContext(UnsavedChangesContext);

  return (
    <Link
      to={to}
      onClick={(event) => {
        onClick?.(event);
        if (event.defaultPrevented || !context?.isDirty) return;
        event.preventDefault();
        context.guardedNavigate(to);
      }}
      {...props}
    />
  );
}
